import { Component } from 'react';
import { isChunkError, reloadOnce } from '@/lib/lazyRetry';

const OB_KEYS = ['vela_ob_active', 'vela_ob_mode'];

// Reports every crash to the backend (so production failures are traceable) and recovers from stale-deploy chunk errors on its own.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, chunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, chunk: isChunkError(error) };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    const chunk = isChunkError(error);
    const url = window.location.pathname + window.location.search;
    // A resumed onboarding step that crashes would crash again on reload — drop the resume flag so the next load starts at Welcome.
    if (url.startsWith('/onboarding') || url.startsWith('/vela/setup')) OB_KEYS.forEach((k) => { try { sessionStorage.removeItem(k); } catch { /* ignore */ } });
    try {
      const token = localStorage.getItem('auth_token');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message: `${error?.name || 'Error'}: ${error?.message || String(error)}`, stack: String(error?.stack || '').slice(0, 6000),
          component_stack: String(info?.componentStack || '').slice(0, 4000), url: window.location.href, user_agent: navigator.userAgent, kind: chunk ? 'chunk_load' : 'render' }),
      }).catch(() => {});
    } catch { /* never let reporting throw */ }
    // Stale index.html after a deploy → old chunk hashes 404. One cache-busting reload fixes it; never loop.
    if (chunk) reloadOnce();
  }

  render() {
    if (this.state.hasError) {
      const chunk = this.state.chunk;
      return (
        <div data-testid="error-boundary-fallback" className="min-h-screen flex items-center justify-center bg-[#0b0b12] px-6">
          <div className="max-w-md text-center">
            <h1 className="text-2xl font-bold text-white mb-2">{chunk ? 'Veluro just updated' : 'Something went wrong'}</h1>
            <p className="text-sm text-white/50 mb-6">
              {chunk ? 'A new version was released while this page was open. Reload once to pick it up — nothing is lost.'
                : 'This section hit an unexpected error. Your data is safe — we\'ve logged it. Reload to keep going, go back to Home, or contact support@veluro.ai if it keeps happening.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => { const u = new URL(window.location.href); u.searchParams.set('_r', String(Date.now())); window.location.replace(u.toString()); }} data-testid="error-boundary-reload"
                className="px-5 py-2.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold">
                Reload page
              </button>
              {!chunk && (
                <button onClick={() => { window.location.assign('/home'); }} data-testid="error-boundary-home"
                  className="px-5 py-2.5 rounded-lg border border-white/15 hover:bg-white/5 text-white/80 text-sm font-semibold">
                  Back to Home
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
